import React, { useState, useEffect, useRef } from 'react';
import { useFileViewer } from '@/contexts/FileViewerContext';
import { 
  X, ZoomIn, ZoomOut, Download, Maximize, Minimize, 
  RotateCw, ChevronLeft, ChevronRight, Loader2, AlertTriangle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const GlobalFileViewer = () => {
  const { isOpen, fileUrl, fileType, fileName, closeViewer } = useFileViewer();
  const [scale, setScale] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [iframeKey, setIframeKey] = useState(0); // Used to force reload iframe
  const containerRef = useRef(null);

  // Reset state when file changes
  useEffect(() => {
    if (isOpen) {
      setScale(1);
      setIsFullScreen(false);
      setLoading(true);
      setError(false);
      setIframeKey(prev => prev + 1);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // For images, we can just detect load. For PDFs in iframe, it's trickier, 
      // but usually the iframe loads quickly if the blob is ready.
      if (fileType === 'image') {
        const img = new Image();
        img.src = fileUrl;
        img.onload = () => setLoading(false);
        img.onerror = () => {
          setLoading(false);
          setError(true);
        };
      } else {
        // For PDFs/Iframes, we set a timeout fallback to clear loading if it hangs
        // The iframe onLoad will also clear it
        const timer = setTimeout(() => setLoading(false), 2000);
        return () => clearTimeout(timer);
      }

    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Keyboard support
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeViewer();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, fileUrl, fileType, closeViewer]);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen().catch(err => console.error(err));
      setIsFullScreen(true);
    } else {
      document.exitFullscreen().catch(err => console.error(err));
      setIsFullScreen(false);
    }
  };

  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to initiate download.");
    }
  };

  const handleReload = () => {
    setLoading(true);
    setError(false);
    setIframeKey(prev => prev + 1);
    setTimeout(() => setLoading(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex flex-col"
        onClick={(e) => e.target === e.currentTarget && closeViewer()}
        ref={containerRef}
      >
        {/* Header / Toolbar */}
        <div className="h-16 flex-shrink-0 bg-navy-steal/90 text-white flex items-center justify-between px-4 lg:px-6 border-b border-white/10 shadow-lg backdrop-blur-md z-50">
          <div className="flex items-center gap-4 min-w-0">
             <div className={cn(
               "p-2 rounded-lg",
               fileType === 'pdf' ? "bg-red-500/20 text-red-400" : "bg-blue-500/20 text-blue-400"
             )}>
               <span className="font-bold text-xs uppercase tracking-wider">
                 {fileType === 'pdf' ? 'PDF Viewer' : 'Image Viewer'}
               </span>
             </div>
             <h3 className="font-medium truncate max-w-[200px] md:max-w-md text-sm md:text-base select-text" title={fileName}>
               {fileName}
             </h3>
          </div>

          <div className="flex items-center gap-2">
            {/* Zoom Controls (Image Only) */}
            {fileType === 'image' && (
              <div className="hidden md:flex items-center gap-1 bg-white/10 rounded-lg p-1 mr-2">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20" onClick={handleZoomOut}>
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <span className="text-xs font-mono w-12 text-center">{Math.round(scale * 100)}%</span>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20" onClick={handleZoomIn}>
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>
            )}

            <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/20" onClick={handleDownload} title="Download Original">
              <Download className="w-4 h-4" />
            </Button>
            
            <Button variant="ghost" size="icon" className="h-9 w-9 text-white hover:bg-white/20 hidden sm:flex" onClick={toggleFullScreen} title="Fullscreen">
              {isFullScreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            </Button>

            <div className="h-6 w-px bg-white/20 mx-2" />

            <Button 
              variant="ghost" 
              size="icon" 
              className="h-9 w-9 text-white hover:bg-red-500/20 hover:text-red-400" 
              onClick={closeViewer}
              title="Close (Esc)"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden flex items-center justify-center relative bg-stone-900" onClick={(e) => e.target === e.currentTarget && closeViewer()}>
           
           {/* Loading Indicator */}
           {loading && (
             <div className="absolute inset-0 flex items-center justify-center z-10 bg-stone-900/50">
                <div className="bg-stone-800 text-white p-4 rounded-lg shadow-xl flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-safety-orange" />
                  <span className="text-sm font-medium">Loading Document...</span>
                </div>
             </div>
           )}

           {/* Error State */}
           {error && (
             <div className="absolute inset-0 flex items-center justify-center z-20">
               <div className="bg-white p-6 rounded-xl shadow-2xl max-w-md text-center">
                 <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                   <AlertTriangle className="w-6 h-6 text-red-600" />
                 </div>
                 <h3 className="text-lg font-bold text-slate-800 mb-2">Failed to Load Document</h3>
                 <p className="text-sm text-slate-500 mb-6">
                   The file could not be rendered. It might be corrupted or in an unsupported format.
                 </p>
                 <div className="flex justify-center gap-3">
                    <Button variant="outline" onClick={handleReload}>
                      <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                    </Button>
                    <Button onClick={handleDownload}>
                      <Download className="w-4 h-4 mr-2" /> Download Instead
                    </Button>
                 </div>
               </div>
             </div>
           )}

           {/* Viewer */}
           {!error && (
             fileType === 'pdf' ? (
               <div className="w-full h-full p-4 md:p-8 flex items-center justify-center">
                  <iframe 
                    key={iframeKey}
                    src={fileUrl}
                    className="w-full h-full max-w-6xl bg-white shadow-2xl rounded-lg border-none" 
                    title="PDF Viewer"
                    onLoad={() => setLoading(false)}
                    onError={() => {
                      setLoading(false);
                      setError(true);
                    }}
                  />
               </div>
             ) : (
               <motion.div 
                 className="relative shadow-2xl overflow-hidden rounded-md"
                 style={{ 
                   maxWidth: '95%', 
                   maxHeight: '90vh' 
                 }}
                 drag
                 dragConstraints={containerRef}
               >
                 <motion.img 
                    src={fileUrl} 
                    alt={fileName}
                    className="w-auto h-auto object-contain max-h-[85vh]"
                    style={{ scale }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    onLoad={() => setLoading(false)}
                    onError={() => {
                      setLoading(false);
                      setError(true);
                    }}
                 />
               </motion.div>
             )
           )}
        </div>

        {/* Footer info for PDF (Overlay) */}
        {fileType === 'pdf' && !error && !loading && (
           <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-xs backdrop-blur-md pointer-events-none border border-white/10 shadow-lg z-10">
              <span className="hidden md:inline">Browser native PDF controls enabled. </span>
              <span>Use toolbar to download if viewing has issues.</span>
           </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default GlobalFileViewer;